/**
 * Time utility functions for Schedule Grid
 *
 * Handles time slot calculations, conversions, and formatting
 * for the 24-hour × 7-day scheduling grid.
 */

export const DAYS_OF_WEEK = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
] as const;

export type DayOfWeek = (typeof DAYS_OF_WEEK)[number];

export const MINUTES_PER_SLOT = 15;
export const SLOTS_PER_HOUR = 60 / MINUTES_PER_SLOT; // 4
export const HOURS_PER_DAY = 24;
export const SLOTS_PER_DAY = HOURS_PER_DAY * SLOTS_PER_HOUR; // 96

/**
 * Convert time string (HH:MM) to row number in grid
 * @param time - Time in HH:MM format (24-hour)
 * @returns Grid row number (1-indexed, accounts for header row)
 */
export const timeToRow = (time: string): number => {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * SLOTS_PER_HOUR + minutes / MINUTES_PER_SLOT + 1; // +1 for header row
};

/**
 * Convert row number to time string
 * @param row - Grid row number (1-indexed)
 * @returns Time in HH:MM format
 */
export const rowToTime = (row: number): string => {
  const slotIndex = row - 1; // Remove header row offset
  const hours = Math.floor(slotIndex / SLOTS_PER_HOUR);
  const minutes = (slotIndex % SLOTS_PER_HOUR) * MINUTES_PER_SLOT;
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
};

/**
 * Convert duration (in hours) to number of grid rows/slots
 * @param duration - Duration in hours (e.g., 1.5 for 90 minutes)
 * @returns Number of grid rows needed
 */
export const durationToSpan = (duration: number): number =>
  Math.ceil(duration * SLOTS_PER_HOUR);

/**
 * Convert number of slots to duration in hours
 * @param slots - Number of 15-minute slots
 * @returns Duration in hours
 */
export const slotsToDuration = (slots: number): number =>
  slots / SLOTS_PER_HOUR;

/**
 * Calculate end time given start time and duration
 * @param startTime - Start time in HH:MM format
 * @param duration - Duration in hours
 * @returns End time in HH:MM:SS format (with :00 seconds for backend compatibility)
 */
export const calculateEndTime = (
  startTime: string,
  duration: number
): string => {
  const startRow = timeToRow(startTime);
  const endRow = startRow + durationToSpan(duration) - 1; // -1 because start row is included
  const timeHHMM = rowToTime(endRow + 1); // +1 to get the time after the last slot
  return `${timeHHMM}:00`; // Add seconds for Django TimeField format
};

/**
 * Format time for display (convert 24-hour to 12-hour with AM/PM)
 * @param time - Time in HH:MM format (24-hour)
 * @returns Formatted time string (e.g., "2:00 PM")
 */
export const formatTimeDisplay = (time: string): string => {
  const [hours24, minutes] = time.split(':').map(Number);

  // Convert to 12-hour format
  let hours12: number;
  if (hours24 === 0) {
    hours12 = 12;
  } else if (hours24 > 12) {
    hours12 = hours24 - 12;
  } else {
    hours12 = hours24;
  }

  const period = hours24 >= 12 ? 'PM' : 'AM';

  // Only show minutes if not on the hour
  if (minutes === 0) {
    return `${hours12} ${period}`;
  }

  return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
};

/**
 * Generate array of time slots for a given hour range
 * @param startHour - Starting hour (0-23)
 * @param endHour - Ending hour (0-23)
 * @param increment - Minutes between slots (default: 60)
 * @returns Array of time strings in HH:MM format
 */
export const generateTimeSlots = (
  startHour: number = 0,
  endHour: number = 24,
  increment: number = 60
): string[] => {
  const slots: string[] = [];
  const slotsPerIncrement = increment / MINUTES_PER_SLOT;

  for (let hour = startHour; hour < endHour; hour += 1) {
    for (
      let slotIndex = 0;
      slotIndex < SLOTS_PER_HOUR;
      slotIndex += slotsPerIncrement
    ) {
      const minutes = slotIndex * MINUTES_PER_SLOT;
      slots.push(
        `${hour.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`
      );
    }
  }

  return slots;
};

/**
 * Check if a time falls within a given range
 * @param time - Time to check (HH:MM)
 * @param startTime - Range start time (HH:MM)
 * @param endTime - Range end time (HH:MM)
 * @returns True if time is within range
 */
export const isTimeInRange = (
  time: string,
  startTime: string,
  endTime: string
): boolean => {
  const timeRow = timeToRow(time);
  const startRow = timeToRow(startTime);
  const endRow = timeToRow(endTime);

  return timeRow >= startRow && timeRow < endRow;
};

/**
 * Check if two time ranges overlap
 * @param start1 - First range start time
 * @param end1 - First range end time
 * @param start2 - Second range start time
 * @param end2 - Second range end time
 * @returns True if ranges overlap
 */
export const doTimeRangesOverlap = (
  start1: string,
  end1: string,
  start2: string,
  end2: string
): boolean => {
  const start1Row = timeToRow(start1);
  const end1Row = timeToRow(end1);
  const start2Row = timeToRow(start2);
  const end2Row = timeToRow(end2);

  return start1Row < end2Row && start2Row < end1Row;
};

/**
 * Round time to nearest time slot
 * @param time - Time in HH:MM format
 * @returns Rounded time in HH:MM format
 */
export const roundToNearestSlot = (time: string): string => {
  const [hours, minutes] = time.split(':').map(Number);
  const roundedMinutes =
    Math.round(minutes / MINUTES_PER_SLOT) * MINUTES_PER_SLOT;

  if (roundedMinutes === 60) {
    return `${(hours + 1).toString().padStart(2, '0')}:00`;
  }

  return `${hours.toString().padStart(2, '0')}:${roundedMinutes.toString().padStart(2, '0')}`;
};

/**
 * Get default visible time range (12 PM - 10 PM for most dance studios)
 */
export const getDefaultTimeRange = () => ({
  startHour: 12, // 12:00 PM
  endHour: 22, // 10:00 PM
});
